import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';
import { generateId } from '../utils/helpers';

const StoreContext = createContext();

const TABLE_MAP = {
  users: 'users_2024',
  categories: 'categories_2024',
  businessUnits: 'business_units_2024',
  taskTemplates: 'task_templates_2024',
  workflowTemplates: 'workflow_templates_2024',
  projects: 'projects_2024',
  workspaces: 'workspaces_2024',
  workspaceMembers: 'workspace_members_2024'
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload, loading: false, dbError: null };
    case 'SET_USER':
      return { ...state, currentUser: action.payload };
    case 'SET_WORKSPACE':
      return { ...state, currentWorkspace: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_DB_ERROR':
      return { ...state, dbError: action.payload, loading: false };
    
    // NEW: Local state updates to prevent re-fetching everything
    case 'UPDATE_LOCAL_ENTITY': {
      const { entityType, item } = action.payload;
      return {
        ...state,
        [entityType]: {
          ...state[entityType],
          [item.id]: item
        }
      };
    }
    case 'DELETE_LOCAL_ENTITY': {
      const { entityType, id } = action.payload;
      const newState = { ...state[entityType] };
      delete newState[id];
      return {
        ...state,
        [entityType]: newState
      };
    }
    default:
      return state;
  }
};

export const StoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    users: {},
    categories: {},
    businessUnits: {},
    taskTemplates: {},
    workflowTemplates: {},
    projects: {},
    workspaces: {},
    workspaceMembers: {},
    currentUser: null,
    currentWorkspace: null,
    loading: true,
    dbError: null
  });

  const isInitialized = useRef(false);
  const lastFetchedWorkspaceId = useRef(null);

  const mapFromDb = (item) => {
    if (!item) return item;
    const mapped = { ...item };
    if (item.logo_url) mapped.logoUrl = item.logo_url;
    if (item.app_url) mapped.appUrl = item.app_url;
    if (item.duration_minutes) mapped.durationMinutes = item.duration_minutes;
    if (item.business_unit_id) mapped.businessUnitId = item.business_unit_id;
    if (item.category_id) mapped.categoryId = item.category_id;
    if (item.workspace_id) mapped.workspaceId = item.workspace_id;
    if (item.start_date) mapped.startDate = item.start_date;
    if (item.is_app_task !== undefined) mapped.isAppTask = item.is_app_task;
    return mapped;
  };

  const mapToDb = (item) => {
    if (!item) return item;
    const dbItem = { ...item };
    const uiFields = ['totalHours', 'totalMinutes', 'calculatedEndDate', 'scheduledWorkflows', 'isFullyComplete', 'hours', 'minutes', 'calculatedStatus', '_wfIndex', '_taskIndex'];
    uiFields.forEach(f => delete dbItem[f]);
    
    if ('logoUrl' in dbItem) { dbItem.logo_url = dbItem.logoUrl; delete dbItem.logoUrl; }
    if ('appUrl' in dbItem) { dbItem.app_url = dbItem.appUrl; delete dbItem.appUrl; }
    if ('durationMinutes' in dbItem) { dbItem.duration_minutes = dbItem.durationMinutes; delete dbItem.durationMinutes; }
    if ('businessUnitId' in dbItem) { dbItem.business_unit_id = dbItem.businessUnitId; delete dbItem.businessUnitId; }
    if ('categoryId' in dbItem) { dbItem.category_id = dbItem.categoryId; delete dbItem.categoryId; }
    if ('workspaceId' in dbItem) { dbItem.workspace_id = dbItem.workspaceId; delete dbItem.workspaceId; }
    if ('startDate' in dbItem) { dbItem.start_date = dbItem.startDate; delete dbItem.startDate; }
    if ('isAppTask' in dbItem) { dbItem.is_app_task = dbItem.isAppTask; delete dbItem.isAppTask; }
    return dbItem;
  };

  const fetchData = useCallback(async (isBackground = false) => {
    if (!state.currentWorkspace || !state.currentUser) return;

    if (!isBackground && !isInitialized.current) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    try {
      const wsId = state.currentWorkspace.id;
      const tables = ['categories', 'businessUnits', 'taskTemplates', 'workflowTemplates', 'projects', 'workspaceMembers'];
      const payload = {};

      for (const key of tables) {
        const { data, error } = await supabase.from(TABLE_MAP[key]).select('*').eq('workspace_id', wsId);
        if (!error) {
          payload[key] = data?.reduce((acc, item) => ({ ...acc, [item.id]: mapFromDb(item) }), {}) || {};
        }
      }

      const memberIds = Object.values(payload.workspaceMembers || {}).map(m => m.user_id);
      if (memberIds.length > 0) {
        const { data: userData } = await supabase.from('users_2024').select('*').in('id', memberIds);
        payload.users = userData?.reduce((acc, u) => ({ ...acc, [u.id]: mapFromDb(u) }), {}) || {};
      }

      dispatch({ type: 'SET_STATE', payload });
      isInitialized.current = true;
      lastFetchedWorkspaceId.current = wsId;
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.currentWorkspace?.id, state.currentUser?.id]); // Only depend on IDs, not object references

  const handleAuthSession = useCallback(async (session) => {
    if (!session?.user) {
      isInitialized.current = false;
      dispatch({ type: 'SET_USER', payload: null });
      dispatch({ type: 'SET_WORKSPACE', payload: null });
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    if (!state.currentUser) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    try {
      let { data: profile } = await supabase.from('users_2024').select('*').eq('id', session.user.id).maybeSingle();
      
      if (!profile) {
        const { data: newProfile } = await supabase.from('users_2024').insert({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || 'User'
        }).select().single();
        profile = newProfile;
      }

      let { data: members } = await supabase.from('workspace_members_2024').select('workspace_id, role').eq('user_id', session.user.id);
      
      let activeWs = null;
      if (!members || members.length === 0) {
        const wsId = generateId('ws');
        await supabase.from('workspaces_2024').insert({ id: wsId, name: 'Main Workspace', owner_id: session.user.id });
        await supabase.from('workspace_members_2024').insert({ id: generateId('wm'), workspace_id: wsId, user_id: session.user.id, role: 'admin' });
        activeWs = { id: wsId, name: 'Main Workspace' };
        profile.role = 'admin';
      } else {
        const { data: ws } = await supabase.from('workspaces_2024').select('*').eq('id', members[0].workspace_id).single();
        activeWs = ws;
        profile.role = members[0].role;
      }

      dispatch({ type: 'SET_USER', payload: mapFromDb(profile) });
      dispatch({ type: 'SET_WORKSPACE', payload: mapFromDb(activeWs) });
    } catch (err) {
      console.error("Auth sync failed:", err);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => handleAuthSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        handleAuthSession(session);
      } else if (event === 'SIGNED_OUT') {
        handleAuthSession(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [handleAuthSession]);

  useEffect(() => {
    // CRITICAL: Only fetch if the workspace ID has actually changed
    if (state.currentWorkspace?.id && state.currentUser?.id && lastFetchedWorkspaceId.current !== state.currentWorkspace.id) {
      fetchData();
    }
  }, [state.currentWorkspace?.id, state.currentUser?.id, fetchData]);

  return (
    <StoreContext.Provider value={{
      state,
      login: (e, p) => supabase.auth.signInWithPassword({ email: e.trim().toLowerCase(), password: p }),
      signup: (u) => supabase.auth.signUp({ email: u.email.trim().toLowerCase(), password: u.password, options: { data: { name: u.name } } }),
      logout: () => { isInitialized.current = false; lastFetchedWorkspaceId.current = null; supabase.auth.signOut(); },
      refresh: () => fetchData(true),
      addEntity: async (type, item) => {
        if (!state.currentWorkspace) throw new Error("No active workspace");
        const newItem = { ...item, id: item.id || generateId(), workspaceId: state.currentWorkspace.id };
        const dbItem = mapToDb(newItem);
        
        const { data, error } = await supabase.from(TABLE_MAP[type]).insert(dbItem).select();
        if (error) throw error;
        
        // Update local state ONLY
        dispatch({ type: 'UPDATE_LOCAL_ENTITY', payload: { entityType: type, item: mapFromDb(data[0]) } });
        return data;
      },
      updateEntity: async (type, item) => {
        const id = item.id;
        const dbItem = mapToDb(item);
        delete dbItem.id;
        
        const { error } = await supabase.from(TABLE_MAP[type]).update(dbItem).eq('id', id);
        if (error) throw error;
        
        // Update local state ONLY
        dispatch({ type: 'UPDATE_LOCAL_ENTITY', payload: { entityType: type, item } });
      },
      deleteEntity: async (type, id) => {
        const { error } = await supabase.from(TABLE_MAP[type]).delete().eq('id', id);
        if (error) throw error;
        
        // Update local state ONLY
        dispatch({ type: 'DELETE_LOCAL_ENTITY', payload: { entityType: type, id } });
      }
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);